import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['input', 'outputContainer', 'logoAssetId']
  static values = { merchantId: String }

  async displayImagePreview(event) {
    event.preventDefault()

    const input = this.inputTarget
    const outputContainer = this.outputContainerTarget

    if (!input.files || !input.files[0]) {
      return
    }

    const file = input.files[0]
    const reader = new FileReader()

    reader.onload = async (e) => {
      const imageSrc = e.target.result

      // Show preview with loading state
      this.showPreviewWithLoading(outputContainer, imageSrc)

      // Upload to S3
      try {
        await this.uploadLogo(file)
        // Remove loading and show final preview
        this.showPreview(outputContainer, imageSrc)
      } catch (error) {
        console.error('Logo upload error:', error)
        const errorMessage = error.message || 'Failed to upload logo. Please try again.'

        // Use Notyf via flash controller if available
        if (window.flashController) {
          window.flashController.showToast(errorMessage, 'error')
        } else {
          // Fallback: dispatch custom event for flash controller to handle
          const event = new CustomEvent('flash:error', {
            detail: { message: errorMessage, context: 'Logo Upload' }
          })
          document.dispatchEvent(event)
        }

        this.clearPreview(outputContainer)
        input.value = ''
      }
    }

    reader.readAsDataURL(file)
  }

  showPreviewWithLoading(outputContainer, imageSrc) {
    // Clear previous preview
    outputContainer.innerHTML = ''

    outputContainer.className = 'flex flex-wrap gap-2 items-center'

    // Create image wrapper with opacity for loading state
    const imageWrapper = document.createElement('div')
    imageWrapper.className = 'rounded-md overflow-hidden w-16 h-16 relative opacity-50'

    // Create image element
    const img = document.createElement('img')
    img.src = imageSrc
    img.className = 'w-full h-full object-cover'

    imageWrapper.appendChild(img)

    // Create loading spinner
    const loadingSpinner = document.createElement('div')
    loadingSpinner.className = 'absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md'
    loadingSpinner.innerHTML = `
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-[#A78CF5]"></div>
    `
    imageWrapper.appendChild(loadingSpinner)

    // Create loading text
    const loadingText = document.createElement('span')
    loadingText.className = 'text-[#A78CF5] text-xs'
    loadingText.textContent = 'Uploading...'

    outputContainer.appendChild(imageWrapper)
    outputContainer.appendChild(loadingText)

    // Remove click action from container
    outputContainer.removeAttribute('data-action')
    outputContainer.classList.remove('cursor-pointer')
  }

  showPreview(outputContainer, imageSrc) {
    // Clear previous preview
    outputContainer.innerHTML = ''

    outputContainer.className = 'flex flex-wrap gap-2 items-center'

    // Create image wrapper
    const imageWrapper = document.createElement('div')
    imageWrapper.className = 'rounded-md overflow-hidden w-16 h-16'

    // Create image element
    const img = document.createElement('img')
    img.src = imageSrc
    img.className = 'w-full h-full object-cover'

    imageWrapper.appendChild(img)

    // Create "Change" span
    const changeSpan = document.createElement('span')
    changeSpan.className = 'text-[#A78CF5] cursor-pointer text-xs'
    changeSpan.dataset.action = 'click->merchant-logo-upload#selectImage'
    changeSpan.textContent = 'Change'

    outputContainer.appendChild(imageWrapper)
    outputContainer.appendChild(changeSpan)

    // Remove click action from container
    outputContainer.removeAttribute('data-action')
    outputContainer.classList.remove('cursor-pointer')
  }

  clearPreview(outputContainer) {
    outputContainer.innerHTML = ''
    outputContainer.className = 'flex gap-2 items-center cursor-pointer'
    outputContainer.setAttribute('data-action', 'click->merchant-logo-upload#selectImage')
    outputContainer.innerHTML = `
      <span class="rounded-md border-2 border-[#A78CF5] px-4 py-3">
        <i class="ph-bold ph-upload-simple text-[#A78CF5]"></i>
      </span>
    `
  }

  selectImage() {
    this.inputTarget.click()
  }

  async uploadLogo(file) {
    // Step 1: Get upload URL
    const uploadUrlResponse = await fetch('/admin/merchants/logo/upload-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({})
    })

    if (!uploadUrlResponse.ok) {
      const errorData = await uploadUrlResponse.json().catch(() => ({}))
      throw new Error(errorData.message || 'Failed to get upload URL')
    }

    const { assetTempId, uploadUrl, uploadHeaders } = await uploadUrlResponse.json()

    // Step 2: Upload to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: uploadHeaders,
      body: file
    })

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload to S3')
    }

    // Step 3: Complete upload and get asset ID
    const merchantId = this.merchantIdValue || null
    const completeUrl = '/admin/merchants/logo/complete'
    const completeBody = {
      tempId: assetTempId,
      name: file.name || 'merchant-logo.png',
      merchantId: merchantId
    }

    const completeResponse = await fetch(completeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(completeBody)
    })

    if (!completeResponse.ok) {
      const errorData = await completeResponse.json().catch(() => ({}))
      throw new Error(errorData.message || 'Failed to complete upload')
    }

    const responseData = await completeResponse.json()
    const logoAssetId = responseData?.merchant?.logoAssetId || responseData.asset?.id

    if (!logoAssetId) {
      throw new Error('No logo asset ID returned')
    }

    // Set the logo asset ID in the hidden field
    const form = this.element.closest('form')

    if (this.hasLogoAssetIdTarget) {
      this.logoAssetIdTarget.value = logoAssetId
    } else {
      // Create hidden field if it doesn't exist
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = 'merchant[logo_asset_id]'
      hiddenField.value = logoAssetId
      hiddenField.id = 'merchant_logo_asset_id'

      if (form) {
        form.appendChild(hiddenField)
      } else {
        // Fallback: append to controller element
        this.element.appendChild(hiddenField)
      }
    }
  }
}

